import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AdministrationService } from '../../core/services/administration.service';
import { NotificationService } from '../../core/services/notification.service';
import { I18nService } from '../../core/services/i18n.service';
import {
  AdministrationOverviewDto,
  FolderAccessAssignmentRequest,
  FolderLookupDto,
  RoleSummaryDto,
  UserSummaryDto
} from '../../core/models/admin.model';

interface EditableFolderAccess {
  folderId: number;
  includeSubfolders: boolean;
}

interface RoleDraft {
  id: number | null;
  name: string;
  description: string;
  permissionIds: number[];
  folderAccesses: EditableFolderAccess[];
}

interface UserDraft {
  id: number | null;
  username: string;
  email: string;
  fullName: string;
  password: string;
  isActive: boolean;
  roleIds: number[];
  folderAccesses: EditableFolderAccess[];
}

@Component({
  selector: 'app-admin-page',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="page-title">
      <h2>{{ i18n.t('admin.title') }}</h2>
      <p>{{ i18n.t('admin.subtitle') }}</p>
    </div>

    <div class="page-grid" *ngIf="overview; else loading">
      <section class="panel list-panel">
        <div class="panel-header">
          <h3>{{ i18n.t('admin.roles') }}</h3>
          <button type="button" (click)="startNewRole()">{{ i18n.t('actions.newRole') }}</button>
        </div>

        <button
          type="button"
          class="list-item"
          *ngFor="let role of overview.roles"
          [class.active]="selectedRoleId === role.id"
          (click)="selectRole(role)">
          <span>{{ role.name }}</span>
          <small>{{ role.userCount === 1 ? i18n.t('admin.usersCount.one') : i18n.t('admin.usersCount.other', { count: role.userCount }) }}</small>
        </button>
      </section>

      <section class="panel editor-panel">
        <div class="panel-header">
          <h3>{{ roleDraft.id ? i18n.t('admin.editRole') : i18n.t('admin.createRole') }}</h3>
        </div>

        <div class="form-grid">
          <label>
            <span>{{ i18n.t('admin.name') }}</span>
            <input [(ngModel)]="roleDraft.name" />
          </label>

          <label>
            <span>{{ i18n.t('admin.description') }}</span>
            <input [(ngModel)]="roleDraft.description" />
          </label>
        </div>

        <div class="section">
          <h4>{{ i18n.t('admin.permissions') }}</h4>
          <div class="chip-grid">
            <label class="chip" *ngFor="let permission of overview.permissions">
              <input
                type="checkbox"
                [checked]="roleHasPermission(permission.id)"
                (change)="toggleRolePermission(permission.id, $event)" />
              <span>{{ permission.code }}</span>
            </label>
          </div>
        </div>

        <div class="section">
          <h4>{{ i18n.t('admin.folderAccess') }}</h4>
          <div class="folder-grid">
            <label class="folder-row" *ngFor="let folder of overview.folders">
              <div class="folder-select">
                <input
                  type="checkbox"
                  [checked]="roleHasFolder(folder.id)"
                  (change)="toggleRoleFolder(folder.id, $event)" />
                <span>{{ folderLabel(folder) }}</span>
              </div>
              <label class="subfolder-toggle" *ngIf="roleHasFolder(folder.id)">
                <input
                  type="checkbox"
                  [checked]="roleIncludeSubfolders(folder.id)"
                  (change)="toggleRoleSubfolders(folder.id, $event)" />
                {{ i18n.t('admin.includeSubfolders') }}
              </label>
            </label>
          </div>
        </div>

        <button type="button" class="save-btn" (click)="saveRole()">{{ i18n.t('actions.saveRole') }}</button>
      </section>

      <section class="panel list-panel">
        <div class="panel-header">
          <h3>{{ i18n.t('admin.users') }}</h3>
          <button type="button" (click)="startNewUser()">{{ i18n.t('actions.newUser') }}</button>
        </div>

        <button
          type="button"
          class="list-item"
          *ngFor="let user of overview.users"
          [class.active]="selectedUserId === user.id"
          (click)="selectUser(user)">
          <span>{{ user.fullName }}</span>
          <small>{{ user.username }}</small>
        </button>
      </section>

      <section class="panel editor-panel">
        <div class="panel-header">
          <h3>{{ userDraft.id ? i18n.t('admin.editUser') : i18n.t('admin.createUser') }}</h3>
        </div>

        <div class="form-grid">
          <label>
            <span>{{ i18n.t('admin.username') }}</span>
            <input [(ngModel)]="userDraft.username" />
          </label>

          <label>
            <span>{{ i18n.t('admin.fullName') }}</span>
            <input [(ngModel)]="userDraft.fullName" />
          </label>

          <label>
            <span>{{ i18n.t('admin.email') }}</span>
            <input [(ngModel)]="userDraft.email" />
          </label>

          <label>
            <span>{{ i18n.t('admin.password') }} {{ userDraft.id ? i18n.t('admin.optional') : '' }}</span>
            <input [(ngModel)]="userDraft.password" type="password" />
          </label>
        </div>

        <label class="switch-row">
          <input type="checkbox" [(ngModel)]="userDraft.isActive" />
          {{ i18n.t('admin.activeUser') }}
        </label>

        <div class="section">
          <h4>Roles</h4>
          <div class="chip-grid">
            <label class="chip" *ngFor="let role of overview.roles">
              <input
                type="checkbox"
                [checked]="userHasRole(role.id)"
                (change)="toggleUserRole(role.id, $event)" />
              <span>{{ role.name }}</span>
            </label>
          </div>
        </div>

        <div class="section">
          <h4>{{ i18n.t('admin.directFolderAccess') }}</h4>
          <div class="folder-grid">
            <label class="folder-row" *ngFor="let folder of overview.folders">
              <div class="folder-select">
                <input
                  type="checkbox"
                  [checked]="userHasFolder(folder.id)"
                  (change)="toggleUserFolder(folder.id, $event)" />
                <span>{{ folderLabel(folder) }}</span>
              </div>
              <label class="subfolder-toggle" *ngIf="userHasFolder(folder.id)">
                <input
                  type="checkbox"
                  [checked]="userIncludeSubfolders(folder.id)"
                  (change)="toggleUserSubfolders(folder.id, $event)" />
                {{ i18n.t('admin.includeSubfolders') }}
              </label>
            </label>
          </div>
        </div>

        <button type="button" class="save-btn" (click)="saveUser()">{{ i18n.t('actions.saveUser') }}</button>
      </section>
    </div>

    <ng-template #loading>
      <div class="panel">{{ i18n.t('admin.loading') }}</div>
    </ng-template>
  `,
  styles: [`
    :host {
      display: block;
    }

    .page-title {
      margin-bottom: 20px;
    }

    .page-grid {
      display: grid;
      grid-template-columns: 280px minmax(0, 1fr) 280px minmax(0, 1fr);
      gap: 18px;
      align-items: start;
    }

    .panel {
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 16px;
      padding: 18px;
    }

    .panel-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      margin-bottom: 14px;
    }

    .panel-header h3,
    .section h4,
    .page-title h2,
    .page-title p {
      margin: 0;
    }

    .page-title p {
      margin-top: 6px;
      color: #64748b;
    }

    .list-panel {
      display: flex;
      flex-direction: column;
      gap: 10px;
      max-height: calc(100vh - 180px);
      overflow: auto;
    }

    .editor-panel {
      max-height: calc(100vh - 180px);
      overflow: auto;
    }

    .list-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      width: 100%;
      padding: 12px 14px;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      background: #f8fafc;
      color: #0f172a;
      cursor: pointer;
    }

    .list-item.active {
      background: #dbeafe;
      border-color: #60a5fa;
    }

    .form-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
      margin-bottom: 16px;
    }

    .form-grid label,
    .folder-row,
    .switch-row {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .form-grid span {
      font-size: 0.92rem;
      color: #475569;
    }

    input {
      height: 40px;
      border: 1px solid #cbd5e1;
      border-radius: 10px;
      padding: 0 12px;
    }

    input[type='checkbox'] {
      width: 16px;
      height: 16px;
      margin: 0;
      padding: 0;
    }

    button {
      border: 0;
      border-radius: 10px;
      background: #0f172a;
      color: #fff;
      padding: 10px 14px;
      cursor: pointer;
    }

    .save-btn {
      margin-top: 18px;
      width: 100%;
    }

    .section {
      margin-top: 18px;
    }

    .chip-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      margin-top: 10px;
    }

    .chip {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 10px;
      border-radius: 999px;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
    }

    .folder-grid {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-top: 10px;
    }

    .folder-row {
      padding: 10px 12px;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      background: #f8fafc;
    }

    .folder-select,
    .subfolder-toggle,
    .switch-row {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .subfolder-toggle {
      margin-left: 26px;
      color: #334155;
    }

    @media (max-width: 1400px) {
      .page-grid {
        grid-template-columns: 1fr 1fr;
      }
    }

    @media (max-width: 900px) {
      .page-grid,
      .form-grid {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class AdminPageComponent {
  private readonly administrationService = inject(AdministrationService);
  private readonly notification = inject(NotificationService);
  readonly i18n = inject(I18nService);

  overview: AdministrationOverviewDto | null = null;
  selectedRoleId: number | null = null;
  selectedUserId: number | null = null;
  roleDraft: RoleDraft = this.createEmptyRoleDraft();
  userDraft: UserDraft = this.createEmptyUserDraft();

  constructor() {
    this.loadOverview();
  }

  loadOverview(): void {
    this.administrationService.getOverview().subscribe({
      next: overview => {
        this.overview = overview;
        if (!this.selectedRoleId && overview.roles.length > 0) {
          this.selectRole(overview.roles[0]);
        }
        if (!this.selectedUserId && overview.users.length > 0) {
          this.selectUser(overview.users[0]);
        }
      },
      error: () => this.notification.error(this.i18n.t('admin.failedLoad'))
    });
  }

  startNewRole(): void {
    this.selectedRoleId = null;
    this.roleDraft = this.createEmptyRoleDraft();
  }

  startNewUser(): void {
    this.selectedUserId = null;
    this.userDraft = this.createEmptyUserDraft();
  }

  selectRole(role: RoleSummaryDto): void {
    this.selectedRoleId = role.id;
    this.roleDraft = {
      id: role.id,
      name: role.name,
      description: role.description ?? '',
      permissionIds: [...role.permissionIds],
      folderAccesses: role.folderAccesses.map(access => ({
        folderId: access.folderId,
        includeSubfolders: access.includeSubfolders
      }))
    };
  }

  selectUser(user: UserSummaryDto): void {
    this.selectedUserId = user.id;
    this.userDraft = {
      id: user.id,
      username: user.username,
      email: user.email,
      fullName: user.fullName,
      password: '',
      isActive: user.isActive,
      roleIds: [...user.roleIds],
      folderAccesses: user.folderAccesses.map(access => ({
        folderId: access.folderId,
        includeSubfolders: access.includeSubfolders
      }))
    };
  }

  roleHasPermission(permissionId: number): boolean {
    return this.roleDraft.permissionIds.includes(permissionId);
  }

  toggleRolePermission(permissionId: number, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.roleDraft.permissionIds = checked
      ? this.uniqueNumbers([...this.roleDraft.permissionIds, permissionId])
      : this.roleDraft.permissionIds.filter(id => id !== permissionId);
  }

  roleHasFolder(folderId: number): boolean {
    return this.roleDraft.folderAccesses.some(access => access.folderId === folderId);
  }

  roleIncludeSubfolders(folderId: number): boolean {
    return this.roleDraft.folderAccesses.find(access => access.folderId === folderId)?.includeSubfolders ?? false;
  }

  toggleRoleFolder(folderId: number, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.roleDraft.folderAccesses = checked
      ? this.uniqueFolderAccesses([...this.roleDraft.folderAccesses, { folderId, includeSubfolders: false }])
      : this.roleDraft.folderAccesses.filter(access => access.folderId !== folderId);
  }

  toggleRoleSubfolders(folderId: number, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.roleDraft.folderAccesses = this.roleDraft.folderAccesses.map(access =>
      access.folderId === folderId ? { ...access, includeSubfolders: checked } : access);
  }

  userHasRole(roleId: number): boolean {
    return this.userDraft.roleIds.includes(roleId);
  }

  toggleUserRole(roleId: number, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.userDraft.roleIds = checked
      ? this.uniqueNumbers([...this.userDraft.roleIds, roleId])
      : this.userDraft.roleIds.filter(id => id !== roleId);
  }

  userHasFolder(folderId: number): boolean {
    return this.userDraft.folderAccesses.some(access => access.folderId === folderId);
  }

  userIncludeSubfolders(folderId: number): boolean {
    return this.userDraft.folderAccesses.find(access => access.folderId === folderId)?.includeSubfolders ?? false;
  }

  toggleUserFolder(folderId: number, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.userDraft.folderAccesses = checked
      ? this.uniqueFolderAccesses([...this.userDraft.folderAccesses, { folderId, includeSubfolders: false }])
      : this.userDraft.folderAccesses.filter(access => access.folderId !== folderId);
  }

  toggleUserSubfolders(folderId: number, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.userDraft.folderAccesses = this.userDraft.folderAccesses.map(access =>
      access.folderId === folderId ? { ...access, includeSubfolders: checked } : access);
  }

  saveRole(): void {
    const request = {
      name: this.roleDraft.name,
      description: this.roleDraft.description || null,
      permissionIds: this.roleDraft.permissionIds,
      folderAccesses: this.toFolderAccessRequests(this.roleDraft.folderAccesses)
    };

    const operation = this.roleDraft.id
      ? this.administrationService.updateRole(this.roleDraft.id, request)
      : this.administrationService.createRole(request);

    operation.subscribe({
      next: role => {
        this.notification.success(this.i18n.t('admin.roleSaved'));
        this.loadOverview();
        this.selectRole(role);
      },
      error: error => this.notification.error(error?.error?.detail ?? this.i18n.t('admin.failedLoad'))
    });
  }

  saveUser(): void {
    const request = {
      username: this.userDraft.username,
      email: this.userDraft.email,
      fullName: this.userDraft.fullName,
      password: this.userDraft.password || null,
      isActive: this.userDraft.isActive,
      roleIds: this.userDraft.roleIds,
      folderAccesses: this.toFolderAccessRequests(this.userDraft.folderAccesses)
    };

    const operation = this.userDraft.id
      ? this.administrationService.updateUser(this.userDraft.id, request)
      : this.administrationService.createUser(request);

    operation.subscribe({
      next: user => {
        this.notification.success(this.i18n.t('admin.userSaved'));
        this.loadOverview();
        this.selectUser(user);
      },
      error: error => this.notification.error(error?.error?.detail ?? this.i18n.t('admin.failedLoad'))
    });
  }

  folderLabel(folder: FolderLookupDto): string {
    return `${' '.repeat(folder.level * 2)}${folder.name}`;
  }

  private toFolderAccessRequests(accesses: EditableFolderAccess[]): FolderAccessAssignmentRequest[] {
    return accesses.map(access => ({
      folderId: access.folderId,
      includeSubfolders: access.includeSubfolders
    }));
  }

  private createEmptyRoleDraft(): RoleDraft {
    return {
      id: null,
      name: '',
      description: '',
      permissionIds: [],
      folderAccesses: []
    };
  }

  private createEmptyUserDraft(): UserDraft {
    return {
      id: null,
      username: '',
      email: '',
      fullName: '',
      password: '',
      isActive: true,
      roleIds: [],
      folderAccesses: []
    };
  }

  private uniqueNumbers(values: number[]): number[] {
    return values.filter((value, index) => values.indexOf(value) === index);
  }

  private uniqueFolderAccesses(values: EditableFolderAccess[]): EditableFolderAccess[] {
    return values.filter((value, index) => values.findIndex(item => item.folderId === value.folderId) === index);
  }
}

